import { isTokenExpired } from '@/utils/jwt.utils'

const BASE_URL = import.meta.env.VITE_API_BASE_URL

// Log da URL configurada (apenas em desenvolvimento)
if (import.meta.env.DEV) {
  console.log('🔧 URL da API configurada:', BASE_URL || 'NÃO CONFIGURADA')
  if (!BASE_URL) {
    console.warn('⚠️ ATENÇÃO: VITE_API_BASE_URL não está definida!')
    console.warn('⚠️ Crie um arquivo .env na pasta elisadape_frontend com:')
    console.warn('⚠️ VITE_API_BASE_URL=http://localhost:3340')
  }
}

// Função para limpar dados de autenticação
function clearAuthData() {
  localStorage.removeItem('auth_token')
  localStorage.removeItem('auth_user')
  localStorage.removeItem('auth_remember_me')
  sessionStorage.removeItem('auth_token')
  sessionStorage.removeItem('auth_user')
}

// Função para obter token de autenticação (localStorage ou sessionStorage)
function getAuthToken(): string | null {
  return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token')
}

// Função para obter usuário autenticado (localStorage ou sessionStorage)
function getAuthUser(): any | null {
  try {
    const userStr = localStorage.getItem('auth_user') || sessionStorage.getItem('auth_user')
    if (userStr) {
      return JSON.parse(userStr)
    }
  } catch (error) {
    console.error('Erro ao obter usuário:', error)
  }
  return null
}

// Função para obter id_empresa do usuário autenticado
function getIdEmpresa(): number | null {
  try {
    const user = getAuthUser()
    if (user) {
      return user.id_empresa || null
    }
  } catch (error) {
    console.error('Erro ao obter id_empresa:', error)
  }
  return null
}

// Função auxiliar para adicionar id_empresa ao body
function addIdEmpresaToBody(body: any): string | undefined {
  if (!body) return undefined

  try {
    const parsedBody = JSON.parse(body)
    const idEmpresa = getIdEmpresa()

    // Adiciona id_empresa se existir e se for um método que modifica dados
    if (idEmpresa && typeof parsedBody === 'object' && parsedBody !== null) {
      parsedBody.id_empresa = idEmpresa
      return JSON.stringify(parsedBody)
    }

    return body
  } catch (error) {
    return body
  }
}

// Função base para fazer requisições à API
export async function apiFetch(path: string, options: RequestInit = {}) {
  // Verifica se a URL base está configurada
  if (!BASE_URL) {
    console.error('❌ VITE_API_BASE_URL não está configurada!')
    throw new Error('URL da API não configurada. Verifique a variável de ambiente VITE_API_BASE_URL.')
  }

  const url = `${BASE_URL}${path}`
  console.log('🌐 Fazendo requisição para:', url)

  const token = getAuthToken()

  // Verifica se o token está expirado antes de fazer a requisição
  if (token && isTokenExpired(token)) {
    // Token expirado, limpa autenticação e redireciona para login
    clearAuthData()
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login'
    }
    throw new Error('Token expirado. Faça login novamente.')
  }

  // Define headers dinamicamente (sem forçar application/json em todos os métodos)
  const headers: Record<string, string> = {
    ...(token && { Authorization: `Bearer ${token}` }),
    'X-App-Path': window.location.pathname || '/',
    ...(options.headers as Record<string, string>)
  }

  // Só adiciona Content-Type se houver body
  if (options.body) {
    headers['Content-Type'] = 'application/json'
  }

  // Adiciona id_empresa ao body se for uma requisição POST ou PUT
  let finalBody = options.body
  if (
    options.method &&
    ['POST', 'PUT', 'PATCH'].includes(options.method) &&
    options.body
  ) {
    finalBody = addIdEmpresaToBody(options.body as string)
  }

  let res: Response
  try {
    res = await fetch(url, {
      credentials: 'include',
      headers,
      ...options,
      body: finalBody
    })
  } catch (fetchError: any) {
    console.error('❌ Erro na requisição fetch:', fetchError)
    // Erro de rede (Failed to fetch)
    if (fetchError.message === 'Failed to fetch' || fetchError.name === 'TypeError') {
      throw new Error(
        `Não foi possível conectar ao servidor. Verifique se o backend está rodando em ${BASE_URL}`
      )
    }
    throw fetchError
  }

  const contentType = res.headers.get('content-type') || ''
  const isJson = contentType.includes('application/json')
  const data = isJson ? await res.json().catch(() => ({})) : await res.text()

  if (!res.ok) {
    // Se for erro 401 (token inválido), limpa autenticação e redireciona para login
    if (res.status === 401) {
      // Limpa dados de autenticação
      localStorage.removeItem('auth_token')
      localStorage.removeItem('auth_user')
      sessionStorage.removeItem('auth_token')
      sessionStorage.removeItem('auth_user')
      
      // Redireciona para login apenas se não estiver já na página de login
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login'
      }
    }
    
    // Preferir message (mensagem específica do backend) sobre error (genérico como "Recurso não encontrado")
    const message =
      (data as any)?.message ||
      (data as any)?.mensagem ||
      (data as any)?.error ||
      res.statusText
    throw new Error(message)
  }

  return data
}

// API de autenticação manual
export const auth = {
  signIn: (body: {
    username: string
    password: string
    rememberMe?: boolean
    empresa_token?: string
  }) =>
    apiFetch('/api/auth/sign-in/username', {
      method: 'POST',
      body: JSON.stringify({
        username: body.username,
        password: body.password,
        rememberMe: body.rememberMe ?? true,
        ...(body.empresa_token
          ? { empresa_token: body.empresa_token }
          : {})
      })
    }),

  signUp: (body: {
    name: string
    email: string
    username: string
    password: string
    rememberMe?: boolean
  }) =>
    apiFetch('/api/auth/sign-up/email', {
      method: 'POST',
      body: JSON.stringify({ ...body, rememberMe: body.rememberMe ?? true })
    }),

  signOut: () => apiFetch('/api/auth/sign-out', { method: 'POST' }),

  session: () => apiFetch('/api/auth/session'),

  isUsernameAvailable: (username: string) =>
    apiFetch('/api/auth/is-username-available', {
      method: 'POST',
      body: JSON.stringify({ username })
    }),

  verifyToken: (token: string) =>
    apiFetch('/api/auth/verify-token', {
      method: 'POST',
      body: JSON.stringify({ token })
    }),

  refreshToken: (token: string) =>
    apiFetch('/api/auth/refresh-token', {
      method: 'POST',
      body: JSON.stringify({ token })
    })
}

// Função para download de arquivos
export async function downloadFile(path: string): Promise<Blob> {
  const url = `${BASE_URL}${path}`
  const token = getAuthToken()

  // Verifica se o token está expirado antes de fazer a requisição
  if (token && isTokenExpired(token)) {
    // Token expirado, limpa autenticação e redireciona para login
    clearAuthData()
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login'
    }
    throw new Error('Token expirado. Faça login novamente.')
  }

  const headers: Record<string, string> = {}
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  headers['X-App-Path'] = window.location.pathname || '/'

  const res = await fetch(url, {
    credentials: 'include',
    headers
  })

  if (!res.ok) {
    // Se for erro 401 (token inválido), limpa autenticação e redireciona para login
    if (res.status === 401) {
      clearAuthData()
      
      // Redireciona para login apenas se não estiver já na página de login
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login'
      }
    }
    
    const error = await res.json().catch(() => ({}))
    throw new Error(error?.message || error?.mensagem || res.statusText)
  }

  return res.blob()
}

// API geral com métodos HTTP
export const api = {
  get: (path: string) => apiFetch(path, { method: 'GET' }),

  post: (path: string, data?: any) =>
    apiFetch(path, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    }),

  put: (path: string, data?: any) =>
    apiFetch(path, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    }),

  delete: (path: string) =>
    apiFetch(path, {
      method: 'DELETE'
    })
}
